<template>
  <div class="detail-page">
    <site-header />

    <section class="detail-hero">
      <div class="core-container detail-hero__body">
        <nav class="detail-breadcrumb" aria-label="面包屑导航">
          <router-link to="/" class="detail-breadcrumb__link">首页</router-link>
          <span class="detail-breadcrumb__sep">&gt;</span>
          <router-link to="/" class="detail-breadcrumb__link">买商标</router-link>
          <span class="detail-breadcrumb__sep">&gt;</span>
          <span v-if="detail" class="detail-breadcrumb__link">
            第{{ formatCategory(detail.category_id) }}类-{{ detail.category_name }}
          </span>
          <span v-if="detail" class="detail-breadcrumb__sep">&gt;</span>
          <span class="detail-breadcrumb__current">{{ detail ? detail.name : '商标详情' }}</span>
        </nav>
      </div>
      <div class="detail-hero__safe" aria-hidden="true" />
    </section>

    <main class="detail-page__main">
      <div class="core-container detail-page__shell">
        <div v-if="loading" class="detail-page__state">
          <div class="detail-page__spinner" />
          <p>正在加载商标详情...</p>
        </div>

        <div v-else-if="error" class="detail-page__state is-error">
          <p>{{ error }}</p>
          <button type="button" class="detail-page__retry" @click="loadDetail">
            重新加载
          </button>
        </div>

        <template v-else-if="detail">
          <div class="detail-card">
          <section class="detail-panel">
            <aside class="detail-panel__left">
              <div class="detail-panel__image-box">
                <img
                  v-if="detail.cover_img"
                  :src="detail.cover_img"
                  :alt="detail.name"
                  class="detail-panel__image"
                />
                <div
                  v-else
                  class="detail-panel__image-fallback"
                  :style="{ background: detail.color }"
                >
                  {{ detail.name }}
                </div>
              </div>

              <div class="detail-panel__meaning">
                <h2 class="detail-panel__meaning-title">
                  {{ detail.name }}商标释义
                </h2>
                <p class="detail-panel__meaning-text">
                  {{ detail.meaning || '暂无商标释义' }}
                </p>
              </div>
            </aside>

            <div class="detail-panel__center">
              <div class="detail-panel__title-row">
                <h1 class="detail-panel__title">{{ detail.name }}</h1>
                <span v-if="detail.is_multi_class" class="detail-panel__multi-tag">
                  多类
                </span>
              </div>

              <div class="detail-panel__price-box">
                <p class="detail-panel__price">
                  直买价：
                  <span class="detail-panel__price-value">
                    <template v-if="detail.price > 0">¥{{ formatPrice(detail.price) }}</template>
                    <template v-else>面议</template>
                  </span>
                </p>
                <p class="detail-panel__fee-note">{{ feeNote }}</p>
              </div>

              <dl class="detail-panel__meta">
                <div class="detail-panel__meta-item">
                  <dt>商标分类</dt>
                  <dd>第{{ formatCategory(detail.category_id) }}类 {{ detail.category_name }}</dd>
                </div>
                <div class="detail-panel__meta-item">
                  <dt>商标类型</dt>
                  <dd>{{ detail.combine_type || '—' }}</dd>
                </div>
                <div class="detail-panel__meta-item">
                  <dt>有效期限</dt>
                  <dd>{{ formatValidity(detail) }}</dd>
                </div>
                <div class="detail-panel__meta-item">
                  <dt>商标编号</dt>
                  <dd>{{ detail.reg_no || detail.id }}</dd>
                </div>
              </dl>

              <div v-if="goodsItems.length" class="detail-panel__section">
                <h3 class="detail-panel__section-title">商品项</h3>
                <div class="detail-panel__goods">
                  <span
                    v-for="(item, index) in goodsItems"
                    :key="`${item}-${index}`"
                    class="detail-panel__goods-tag"
                  >{{ item }}</span>
                </div>
              </div>

              <div v-if="detail.related_classes.length" class="detail-panel__section">
                <h3 class="detail-panel__section-title">相关分类</h3>
                <div class="detail-panel__classes">
                  <button
                    v-for="item in detail.related_classes"
                    :key="item.id"
                    type="button"
                    :class="[
                      'detail-panel__class-card',
                      { 'is-active': item.id === detail.id }
                    ]"
                    @click="switchRelatedClass(item)"
                  >
                    <span class="detail-panel__class-name">
                      {{ formatCategory(item.category_id) }}类-{{ item.category_name }}
                    </span>
                    <span class="detail-panel__class-price">
                      <template v-if="item.price > 0">¥{{ formatPrice(item.price) }}</template>
                      <template v-else>面议</template>
                    </span>
                    <span
                      v-if="item.id === detail.id"
                      class="detail-panel__class-check"
                      aria-hidden="true"
                    >✓</span>
                  </button>
                </div>
              </div>

              <div class="detail-panel__actions">
                <button
                  type="button"
                  class="detail-panel__btn detail-panel__btn--buy"
                  @click="onBuy"
                >我要购买</button>
                <button
                  type="button"
                  class="detail-panel__btn detail-panel__btn--favorite"
                  :class="{ 'is-active': isFavorited }"
                  @click="onFavorite"
                >
                  <span class="detail-panel__btn-icon" aria-hidden="true">{{ isFavorited ? '★' : '☆' }}</span>
                  {{ isFavorited ? '已收藏' : '收藏商标' }}
                </button>
              </div>
            </div>

            <aside class="detail-panel__right">
              <div class="detail-consultant">
                <div class="detail-consultant__avatar" aria-hidden="true">
                  {{ consultantInitial }}
                </div>
                <p class="detail-consultant__name">
                  {{ consultant.title }} {{ consultant.name }}
                </p>
                <a
                  :href="'tel:' + consultant.phone"
                  class="detail-consultant__phone"
                >{{ consultant.phone }}</a>
                <a
                  :href="'tel:' + consultant.phone"
                  class="detail-consultant__btn"
                >在线咨询顾问</a>
                <img
                  :src="consultant.qrImage"
                  :alt="consultant.qrHint"
                  class="detail-consultant__qr"
                />
                <p class="detail-consultant__qr-hint">{{ consultant.qrHint }}</p>
              </div>
            </aside>
          </section>

          <section class="detail-trust">
            <div class="detail-trust__content">
              <span
                v-for="item in trustItems"
                :key="item.label"
                class="detail-trust__item"
              >
                <img
                  :src="item.icon"
                  :alt="item.label"
                  class="detail-trust__icon"
                />
                {{ item.label }}
              </span>
              <span class="detail-trust__note">
                <img
                  :src="trustBadge"
                  alt=""
                  class="detail-trust__badge"
                  aria-hidden="true"
                />
                {{ trustNote }}
              </span>
            </div>
          </section>
          </div>

          <section class="detail-lower">
            <detail-extra-panel :detail="detail" />
            <detail-recommend-sidebar :detail="detail" />
          </section>
        </template>
      </div>
    </main>

    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import DetailExtraPanel from '@/components/detail/DetailExtraPanel.vue'
import DetailRecommendSidebar from '@/components/detail/DetailRecommendSidebar.vue'
import { fetchTrademarkDetail, fetchTenantContact } from '@/api/trademark'
import {
  DETAIL_CONSULTANT_FALLBACK,
  DETAIL_FEE_NOTE,
  DETAIL_TRUST_BADGE,
  DETAIL_TRUST_ITEMS,
  DETAIL_TRUST_NOTE
} from '@/constants/detailPage'
import { goTrademarkDetail, scrollPageToTop } from '@/utils/pageNavigation'
import { isLoggedIn } from '@/utils/auth'
import { isFavorite, toggleFavorite } from '@/utils/favorite'

export default {
  name: 'TrademarkDetailPage',
  components: {
    SiteHeader,
    SiteFooter,
    DetailExtraPanel,
    DetailRecommendSidebar
  },
  data() {
    return {
      detail: null,
      loading: false,
      error: null,
      consultant: { ...DETAIL_CONSULTANT_FALLBACK },
      isFavorited: false,
      feeNote: DETAIL_FEE_NOTE,
      trustItems: DETAIL_TRUST_ITEMS,
      trustBadge: DETAIL_TRUST_BADGE,
      trustNote: DETAIL_TRUST_NOTE
    }
  },
  computed: {
    goodsItems() {
      if (!this.detail || !this.detail.items_detail) return []
      return this.detail.items_detail.map(item => this.formatGoodsItem(item))
    },
    consultantInitial() {
      return this.consultant.name ? this.consultant.name.charAt(0) : '顾'
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        scrollPageToTop()
        this.loadDetail()
      }
    }
  },
  created() {
    scrollPageToTop()
    this.loadContact()
  },
  mounted() {
    scrollPageToTop()
  },
  methods: {
    loadContact() {
      fetchTenantContact()
        .then(result => {
          if (result.status !== 1 || !result.data) return
          const contact = result.data
          this.consultant = {
            title: '商标顾问',
            name: contact.contact_name || contact.contact_wechat || DETAIL_CONSULTANT_FALLBACK.name,
            phone: contact.contact_phone || '',
            qrImage: contact.wechat_qrcode || DETAIL_CONSULTANT_FALLBACK.qrImage,
            qrHint: DETAIL_CONSULTANT_FALLBACK.qrHint
          }
        })
        .catch(() => {})
    },
    loadDetail() {
      const proId = this.$route.params.id
      if (!proId) {
        this.error = '缺少商标 ID'
        return
      }

      this.loading = true
      this.error = null

      fetchTrademarkDetail(proId)
        .then(result => {
          if (result.status !== 1 || !result.data) {
            this.error = result.desc || '加载详情失败'
            this.detail = null
            return
          }

          this.detail = result.data
          this.isFavorited = isFavorite(this.detail.reg_no || this.detail.id)
          document.title = `${this.detail.name} - 商标详情 - 懂标帝`
        })
        .catch(() => {
          this.error = '网络错误，请稍后重试'
          this.detail = null
        })
        .finally(() => {
          this.loading = false
          scrollPageToTop()
        })
    },
    switchRelatedClass(item) {
      if (!item || item.id === this.detail.id) return
      goTrademarkDetail(item.id)
    },
    onBuy() {
      if (!this.detail) return
      const redirect = `/order?pro_id=${this.detail.id}`
      if (!isLoggedIn()) {
        this.$store.commit('SHOW_LOGIN', redirect)
        return
      }
      this.$router.push(redirect)
    },
    onFavorite() {
      if (!this.detail) return
      const result = toggleFavorite(this.detail.reg_no || this.detail.id)
      this.isFavorited = result.favorited
    },
    formatCategory(id) {
      return String(id).padStart(2, '0')
    },
    formatPrice(price) {
      if (!price || price <= 0) return '面议'
      if (price >= 10000) {
        return (price / 10000).toFixed(2) + '万'
      }
      return Math.round(price).toLocaleString('zh-CN')
    },
    formatValidity(detail) {
      const begin = detail.valid_date_begin
      const end = detail.valid_date
      if (begin && end) return `${begin} 至 ${end}`
      if (end) return end
      return '—'
    },
    formatGoodsItem(item) {
      if (!item) return ''
      if (typeof item === 'string') return item
      const code = item.goodsCode || item.goods_code || item.code || ''
      const name = item.goodsName || item.name || ''
      if (code && name) return `${code} ${name}`
      return name || code || ''
    }
  }
}
</script>

<style scoped>
.detail-page {
  min-height: 100vh;
  background: var(--color-bg-main);
}

.detail-hero {
  background: linear-gradient(
    90deg,
    var(--color-banner-fill-left) 0%,
    var(--color-banner-fill-left) 42%,
    var(--color-banner-fill-right) 58%,
    var(--color-banner-fill-right) 100%
  );
}

.detail-hero__body {
  display: flex;
  align-items: flex-end;
  min-height: var(--detail-hero-body-height);
  padding-bottom: var(--space-sm);
}

.detail-hero__safe {
  height: var(--detail-hero-safe-bottom);
  background: linear-gradient(
    90deg,
    var(--color-banner-fill-left) 0%,
    var(--color-banner-fill-left) 42%,
    var(--color-banner-fill-right) 58%,
    var(--color-banner-fill-right) 100%
  );
}

.detail-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-sm);
  min-height: var(--detail-breadcrumb-height);
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.88);
}

.detail-breadcrumb__link {
  color: rgba(255, 255, 255, 0.88);
  transition: color 0.2s;
}

.detail-breadcrumb__link:hover {
  color: var(--color-text-inverse);
}

.detail-breadcrumb__sep {
  opacity: 0.72;
}

.detail-breadcrumb__current {
  color: var(--color-text-inverse);
}

.detail-page__main {
  position: relative;
  z-index: 2;
  margin-top: calc(-1 * var(--detail-hero-safe-bottom));
  padding-bottom: var(--space-xl);
}

.detail-page__shell {
  transform: translateY(calc(-1 * var(--detail-panel-lift)));
}

.detail-page__state {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
  background: var(--detail-panel-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
}

.detail-page__state.is-error {
  color: #dc2626;
}

.detail-page__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: detail-spin 0.8s linear infinite;
  margin-bottom: var(--space-md);
}

.detail-page__retry {
  margin-top: var(--space-md);
  padding: var(--space-sm) var(--space-lg);
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: var(--font-size-sm);
}

@keyframes detail-spin {
  to { transform: rotate(360deg); }
}

.detail-card {
  background: var(--detail-panel-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  overflow: hidden;
}

.detail-panel {
  display: grid;
  grid-template-columns: var(--detail-grid-columns);
  gap: var(--detail-grid-gap);
  padding: var(--space-lg);
}

.detail-panel__left {
  min-width: 0;
}

.detail-panel__image-box {
  width: 100%;
  min-height: var(--detail-image-size);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-page);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-md);
}

.detail-panel__image {
  max-width: 100%;
  max-height: var(--detail-image-size);
  object-fit: contain;
}

.detail-panel__image-fallback {
  width: 100%;
  min-height: calc(var(--detail-image-size) - var(--space-md) * 2);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  color: var(--color-text-inverse);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  text-align: center;
  padding: var(--space-md);
  word-break: break-all;
}

.detail-panel__meaning {
  margin-top: var(--space-lg);
}

.detail-panel__meaning-title {
  font-size: var(--font-size-md);
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--space-sm);
}

.detail-panel__meaning-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.8;
}

.detail-panel__center {
  min-width: 0;
  padding: 0 var(--space-sm);
}

.detail-panel__title-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
}

.detail-panel__title {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-text-primary);
}

.detail-panel__multi-tag {
  padding: 2px var(--space-sm);
  border-radius: var(--radius-sm);
  background: #fff4e8;
  color: var(--color-accent-orange);
  font-size: var(--font-size-xs);
  font-weight: 600;
}

.detail-panel__price-box {
  background: #f7f8fa;
  border-radius: var(--radius-md);
  padding: var(--space-md);
  margin-bottom: var(--space-lg);
}

.detail-panel__price {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}

.detail-panel__price-value {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-price);
}

.detail-panel__fee-note {
  margin-top: var(--space-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.detail-panel__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-md) var(--space-lg);
  margin-bottom: var(--space-lg);
}

.detail-panel__meta-item {
  display: flex;
  gap: var(--space-sm);
  font-size: var(--font-size-sm);
}

.detail-panel__meta-item dt {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.detail-panel__meta-item dd {
  color: var(--color-text-primary);
  word-break: break-word;
}

.detail-panel__section {
  margin-bottom: var(--space-lg);
}

.detail-panel__section-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--space-sm);
}

.detail-panel__goods {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.detail-panel__goods-tag {
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid #c5d9f6;
  border-radius: var(--radius-sm);
  background: var(--color-primary-light);
  color: var(--color-primary);
  font-size: var(--font-size-xs);
}

.detail-panel__classes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.detail-panel__class-card {
  position: relative;
  min-width: 148px;
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-page);
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.detail-panel__class-card:hover {
  border-color: var(--color-price);
}

.detail-panel__class-card.is-active {
  border-color: var(--color-price);
  box-shadow: 0 0 0 1px var(--color-price);
}

.detail-panel__class-name {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

.detail-panel__class-price {
  display: block;
  margin-top: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: var(--color-price);
}

.detail-panel__class-check {
  position: absolute;
  top: 0;
  right: 0;
  width: 18px;
  height: 18px;
  background: var(--color-price);
  color: var(--color-text-inverse);
  font-size: 10px;
  line-height: 18px;
  text-align: center;
  border-radius: 0 var(--radius-sm) 0 var(--radius-sm);
}

.detail-panel__actions {
  display: flex;
  gap: var(--space-md);
  margin-top: var(--space-lg);
}

.detail-panel__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
  min-width: 148px;
  height: 44px;
  padding: 0 var(--space-lg);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-md);
  font-weight: 600;
  transition: opacity 0.2s, background 0.2s;
}

.detail-panel__btn--buy {
  background: var(--color-price);
  color: var(--color-text-inverse);
  border: none;
}

.detail-panel__btn--buy:hover {
  opacity: 0.92;
}

.detail-panel__btn--favorite {
  background: var(--color-bg-page);
  color: var(--color-price);
  border: 1px solid var(--color-price);
}

.detail-panel__btn--favorite:hover,
.detail-panel__btn--favorite.is-active {
  background: #fff5f0;
}

.detail-panel__btn-icon {
  font-size: var(--font-size-lg);
  line-height: 1;
}

.detail-panel__right {
  min-width: 0;
  border-left: 1px dashed var(--color-border);
  padding-left: var(--space-lg);
}

.detail-consultant {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.detail-consultant__avatar {
  width: var(--detail-consultant-avatar-size);
  height: var(--detail-consultant-avatar-size);
  border-radius: 50%;
  background: linear-gradient(135deg, #6ea8ff, var(--color-primary));
  color: var(--color-text-inverse);
  font-size: var(--font-size-xl);
  font-weight: 700;
  line-height: var(--detail-consultant-avatar-size);
}

.detail-consultant__name {
  margin-top: var(--space-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
}

.detail-consultant__phone {
  margin-top: var(--space-xs);
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}

.detail-consultant__phone:hover {
  color: var(--color-primary);
}

.detail-consultant__btn {
  margin-top: var(--space-md);
  width: 100%;
  height: 36px;
  line-height: 34px;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-sm);
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  background: var(--color-bg-page);
}

.detail-consultant__btn:hover {
  background: var(--color-primary-light);
}

.detail-consultant__qr {
  margin-top: var(--space-lg);
  width: var(--detail-consultant-qr-size);
  height: var(--detail-consultant-qr-size);
  object-fit: contain;
  border: 1px solid var(--color-border-light);
}

.detail-consultant__qr-hint {
  margin-top: var(--space-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.detail-trust {
  padding: var(--detail-trust-padding-y) var(--detail-trust-padding-x);
  background: var(--detail-trust-bar-bg);
  border-top: var(--detail-trust-divider);
}

.detail-trust__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-md) var(--space-lg);
}

.detail-trust__item,
.detail-trust__note {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.detail-trust__item {
  color: var(--detail-trust-text);
}

.detail-trust__icon {
  width: var(--detail-trust-icon-size);
  height: var(--detail-trust-icon-size);
  flex-shrink: 0;
  object-fit: contain;
}

.detail-trust__note {
  color: var(--detail-trust-note-text);
  margin-left: auto;
}

.detail-trust__badge {
  width: var(--detail-trust-badge-size);
  height: var(--detail-trust-badge-size);
  flex-shrink: 0;
  object-fit: contain;
}

.detail-lower {
  margin-top: var(--space-lg);
  display: grid;
  grid-template-columns: var(--detail-lower-grid-columns);
  gap: var(--detail-lower-gap);
  align-items: start;
}
</style>
